import { useMeQuery } from '../queries/use-me.js';

/**
 * Profile page — calls `GET /v1/me` through the authenticated API client
 * and renders the response. Smoke test for the SPA → MSAL token →
 * AGW JWT verify → Lambda → JSON-response chain.
 */
export function Profile(): JSX.Element {
  const { data, error, isLoading, isError } = useMeQuery();

  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="mb-6 text-3xl font-bold text-gray-900 dark:text-gray-100">Profile</h1>

      {isLoading ? (
        <p className="text-gray-600 dark:text-gray-400" role="status">
          Loading…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load profile'}
        </div>
      ) : data ? (
        <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Object ID
            </dt>
            <dd className="break-all font-mono text-sm text-gray-900 dark:text-gray-100">
              {data.id}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Subject
            </dt>
            <dd className="break-all font-mono text-sm text-gray-900 dark:text-gray-100">
              {data.sub}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Name
            </dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">{data.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Email
            </dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">{data.email ?? '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Roles
            </dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">
              {data.roles.length === 0 ? (
                <span className="text-gray-500 dark:text-gray-400">No roles assigned</span>
              ) : (
                <span className="inline-flex flex-wrap gap-2">
                  {data.roles.map((role) => (
                    <span
                      key={role}
                      className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {role}
                    </span>
                  ))}
                </span>
              )}
            </dd>
          </div>
        </dl>
      ) : null}
    </section>
  );
}
